import dayjs from "dayjs";
import { getWorkplaceNumber } from "@core/runtime-settings";

export const DATE_MODE = {
  DOCDATE: 0,
  DATE_BRON: 1,
  DATE_BEG: 2,
  DATE_END: 3
};

export const DATE_MODE_OPTIONS = [
  { value: DATE_MODE.DOCDATE, label: "Путёвка" },
  { value: DATE_MODE.DATE_BRON, label: "Бронь" },
  { value: DATE_MODE.DATE_BEG, label: "Заезд" },
  { value: DATE_MODE.DATE_END, label: "Выезд" }
];

export const STATUS = {
  CANCELLED: -1,
  NEW: 0,
  CONFIRMED: 1,
  CHECKED_IN: 2,
  CHECKED_OUT: 3
};

export const STATUS_LABELS = {
  [STATUS.CANCELLED]: "Отменен",
  [STATUS.NEW]: "Новый",
  [STATUS.CONFIRMED]: "Подтверждение",
  [STATUS.CHECKED_IN]: "Заселение",
  [STATUS.CHECKED_OUT]: "Выселен"
};

/** Подписи и тона бейджей журнала (макет Figma). */
export const JOURNAL_STATUS_META = {
  [STATUS.NEW]: { label: "Новый", tone: "new" },
  [STATUS.CONFIRMED]: { label: "Подтверждён", tone: "confirmed" },
  [STATUS.CHECKED_IN]: { label: "Заселен", tone: "in" },
  [STATUS.CHECKED_OUT]: { label: "Выселен", tone: "out" },
  [STATUS.CANCELLED]: { label: "Отменен", tone: "cancel" }
};

export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Все статусы" },
  { value: STATUS.NEW, label: JOURNAL_STATUS_META[STATUS.NEW].label },
  { value: STATUS.CONFIRMED, label: JOURNAL_STATUS_META[STATUS.CONFIRMED].label },
  { value: STATUS.CHECKED_IN, label: JOURNAL_STATUS_META[STATUS.CHECKED_IN].label },
  { value: STATUS.CHECKED_OUT, label: JOURNAL_STATUS_META[STATUS.CHECKED_OUT].label },
  { value: STATUS.CANCELLED, label: JOURNAL_STATUS_META[STATUS.CANCELLED].label }
];

export const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

export const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const toFlag = (value) => Number(value) === 1;

export const formatDate = (value) => {
  if (!value) return "";
  // Prefer calendar date from ISO prefix so UTC midnight does not shift the day.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = dayjs(value.slice(0, 10), "YYYY-MM-DD");
    return d.isValid() ? d.format("DD.MM.YYYY") : "";
  }
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY") : "";
};

export const formatDateTime = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY HH:mm") : "";
};

export const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatMoneyRub = (value) => {
  const formatted = formatMoney(value);
  return formatted ? `${formatted} ₽` : "";
};

/** 0 — ночи, 1 — койкодни (SPR_SERVIS.RAZMER, vid=1). Delphi GetSrok. */
export const stayDayPlus = (razmer) => {
  const n = Number(razmer);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n >= 1 ? 1 : 0;
};

export const stayDuration = (beg, end, razmer) => {
  if (!beg || !end) return null;
  const a = dayjs(beg).startOf("day");
  const b = dayjs(end).startOf("day");
  if (!a.isValid() || !b.isValid()) return null;
  const nights = b.diff(a, "day");
  if (nights < 0) return null;
  const plus = stayDayPlus(razmer);
  const n = nights + plus;
  if (n <= 0) return null;
  const isBedDays = plus === 1;
  return {
    n,
    isBedDays,
    unit: isBedDays ? "кд" : "н"
  };
};

export const todayIso = () => dayjs().format("YYYY-MM-DD");

/** Date-only for API: keep calendar day (avoid toISOString UTC −1 day). */
export const toApiDate = (value) => {
  if (!value) return null;
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

/** Local date+time for API without UTC conversion. */
export const toApiDateTime = (value) => {
  if (!value) return null;
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DDTHH:mm:ss") : null;
};

export const FILTERS_STORAGE_KEY = "planOnline.doc.exp.filters";
export const GRID_STORAGE_KEY = "planOnline.doc.exp.grid.v5";

export const defaultFilters = () => ({
  dateMode: DATE_MODE.DOCDATE,
  dateBeg: todayIso(),
  dateEnd: todayIso(),
  hideCancelled: true,
  idStatus: null,
  vidPutevki: 0,
  pay: null,
  idBranch: 0,
  q: "",
  sortBy: "docdate",
  sortDir: "asc"
});

const isIsoDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const FILTER_URL_KEYS = [
  "dateMode",
  "dateBeg",
  "dateEnd",
  "q",
  "idStatus",
  "vidPutevki",
  "pay",
  "idBranch",
  "sortBy",
  "sortDir"
];

const normalizeFiltersShape = (parsed, base = defaultFilters()) => {
  const dateMode = Number(parsed.dateMode);
  const hasExplicitBranch = Object.prototype.hasOwnProperty.call(parsed, "idBranch");
  const idBranch = Number(parsed.idBranch);
  const sortBy =
    typeof parsed.sortBy === "string" && parsed.sortBy.trim()
      ? parsed.sortBy.trim()
      : base.sortBy;
  const sortDir = parsed.sortDir === "desc" ? "desc" : "asc";
  const idStatusRaw = parsed.idStatus;
  const idStatusNum = Number(idStatusRaw);
  const idStatus =
    idStatusRaw === "" || idStatusRaw === null || idStatusRaw === undefined
      ? null
      : Number.isFinite(idStatusNum) && idStatusNum >= -1 && idStatusNum <= 3
        ? idStatusNum
        : null;
  const vidPutevki = Number(parsed.vidPutevki);
  const pay =
    parsed.pay === "paid" || parsed.pay === "unpaid"
      ? parsed.pay
      : parsed.unpaidOnly
        ? "unpaid"
        : null;

  return {
    ...base,
    dateMode: dateMode >= 0 && dateMode <= 3 ? dateMode : base.dateMode,
    dateBeg: isIsoDate(parsed.dateBeg) ? parsed.dateBeg : base.dateBeg,
    dateEnd: isIsoDate(parsed.dateEnd) ? parsed.dateEnd : base.dateEnd,
    hideCancelled: idStatus !== STATUS.CANCELLED,
    idStatus,
    vidPutevki: Number.isFinite(vidPutevki) && vidPutevki > 0 ? vidPutevki : 0,
    pay,
    idBranch:
      hasExplicitBranch && Number.isFinite(idBranch) && idBranch >= 0
        ? idBranch
        : base.idBranch,
    q: typeof parsed.q === "string" ? parsed.q : "",
    sortBy,
    sortDir
  };
};

export const loadStoredFilters = () => {
  const base = {
    ...defaultFilters(),
    idBranch: getWorkplaceNumber("idBranch", 0)
  };
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw);
    return normalizeFiltersShape(parsed, base);
  } catch {
    return base;
  }
};

export const saveFilters = (filters) => {
  try {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        dateMode: filters.dateMode,
        dateBeg: filters.dateBeg,
        dateEnd: filters.dateEnd,
        hideCancelled: filters.idStatus !== STATUS.CANCELLED,
        idStatus: filters.idStatus,
        vidPutevki: filters.vidPutevki,
        pay: filters.pay === "paid" || filters.pay === "unpaid" ? filters.pay : null,
        idBranch: filters.idBranch,
        q: filters.q,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir
      })
    );
  } catch {
    /* ignore */
  }
};

/** Сериализация фильтров журнала в query-параметры. */
export const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const dateMode = Number(filters?.dateMode);
  params.set("dateMode", String(dateMode >= 0 && dateMode <= 3 ? dateMode : DATE_MODE.DOCDATE));
  if (isIsoDate(filters?.dateBeg)) params.set("dateBeg", filters.dateBeg);
  if (isIsoDate(filters?.dateEnd)) params.set("dateEnd", filters.dateEnd);
  const q = typeof filters?.q === "string" ? filters.q.trim() : "";
  if (q) params.set("q", q);
  if (filters?.idStatus != null && filters.idStatus !== "") {
    params.set("idStatus", String(filters.idStatus));
  }
  if (Number(filters?.vidPutevki) > 0) params.set("vidPutevki", String(Number(filters.vidPutevki)));
  if (filters?.pay === "paid" || filters?.pay === "unpaid") params.set("pay", filters.pay);
  if (Number(filters?.idBranch) > 0) params.set("idBranch", String(Number(filters.idBranch)));
  if (typeof filters?.sortBy === "string" && filters.sortBy && filters.sortBy !== "docdate") {
    params.set("sortBy", filters.sortBy);
  }
  if (filters?.sortDir === "desc") params.set("sortDir", "desc");
  return params;
};

/** Разбор фильтров из query. null — если в URL нет известных ключей. */
export const filtersFromSearchParams = (searchParams, fallback) => {
  const params =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams || "");
  const hasAny = FILTER_URL_KEYS.some((key) => params.has(key));
  if (!hasAny) return null;

  const base = {
    ...defaultFilters(),
    idBranch: getWorkplaceNumber("idBranch", 0),
    ...(fallback || {})
  };

  const raw = {
    dateMode: params.has("dateMode") ? params.get("dateMode") : base.dateMode,
    dateBeg: params.has("dateBeg") ? params.get("dateBeg") : base.dateBeg,
    dateEnd: params.has("dateEnd") ? params.get("dateEnd") : base.dateEnd,
    q: params.has("q") ? params.get("q") ?? "" : "",
    idStatus: params.has("idStatus") ? params.get("idStatus") : null,
    vidPutevki: params.has("vidPutevki") ? params.get("vidPutevki") : 0,
    pay: params.has("pay") ? params.get("pay") : null,
    idBranch: params.has("idBranch") ? params.get("idBranch") : 0,
    sortBy: params.has("sortBy") ? params.get("sortBy") : "docdate",
    sortDir: params.has("sortDir") ? params.get("sortDir") : "asc"
  };

  return normalizeFiltersShape(raw, base);
};

export const filtersSearchEquals = (a, b) =>
  filtersToSearchParams(a).toString() === filtersToSearchParams(b).toString();

export const filtersSearchString = (filters) => {
  const s = filtersToSearchParams(filters).toString();
  return s ? `?${s}` : "";
};

export const normalizeListRow = (row) => ({
  isClosed: toFlag(pick(row, "isClosed", "IsClosed")),
  usrStatusColor: toNumber(pick(row, "usrStatusColor", "UsrStatusColor")),
  usrStatusName: pick(row, "usrStatusName", "UsrStatusName") ?? "",
  idDocPack: toNumber(pick(row, "idDocPack", "IdDocPack")) ?? 0,
  idDoc: toNumber(pick(row, "idDoc", "IdDoc")),
  idStatus: toNumber(pick(row, "idStatus", "IdStatus")) ?? 0,
  statusName: pick(row, "statusName", "StatusName") ?? STATUS_LABELS[0],
  docnum: toNumber(pick(row, "docnum", "Docnum")) ?? 0,
  docdate: pick(row, "docdate", "Docdate") ?? null,
  dateBron: pick(row, "dateBron", "DateBron") ?? null,
  dateBegDate: pick(row, "dateBegDate", "DateBegDate") ?? null,
  dateEndDate: pick(row, "dateEndDate", "DateEndDate") ?? null,
  predName: pick(row, "predName", "PredName") ?? "",
  predKomment: pick(row, "predKomment", "PredKomment") ?? "",
  syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
  symaPlat: toNumber(pick(row, "symaPlat", "SymaPlat")) ?? 0,
  symaDolg: toNumber(pick(row, "symaDolg", "SymaDolg")) ?? 0,
  avansOpen: toNumber(pick(row, "avansOpen", "AvansOpen")) ?? 0,
  komment: pick(row, "komment", "Komment") ?? "",
  numberName: pick(row, "numberName", "NumberName") ?? "",
  catName: pick(row, "catName", "CatName") ?? "",
  dateBeg: pick(row, "dateBeg", "DateBeg") ?? null,
  dateEnd: pick(row, "dateEnd", "DateEnd") ?? null,
  dateBegFact: pick(row, "dateBegFact", "DateBegFact") ?? null,
  dateEndFact: pick(row, "dateEndFact", "DateEndFact") ?? null,
  vidPutevkiName: pick(row, "vidPutevkiName", "VidPutevkiName") ?? "",
  vidPutevkiRazmer: stayDayPlus(pick(row, "vidPutevkiRazmer", "VidPutevkiRazmer")),
  skidkaName: pick(row, "skidkaName", "SkidkaName") ?? "",
  prcSkidka: toNumber(pick(row, "prcSkidka", "PrcSkidka")) ?? 0,
  avtorName: pick(row, "avtorName", "AvtorName") ?? "",
  vidPitaniaName: pick(row, "vidPitaniaName", "VidPitaniaName") ?? "",
  e1: toFlag(pick(row, "e1", "E1")),
  e2: toFlag(pick(row, "e2", "E2")),
  e3: toFlag(pick(row, "e3", "E3")),
  e4: toFlag(pick(row, "e4", "E4")),
  cntOsn: toNumber(pick(row, "cntOsn", "CntOsn")) ?? 0,
  cntDop: toNumber(pick(row, "cntDop", "CntDop")) ?? 0,
  cntBaby: toNumber(pick(row, "cntBaby", "CntBaby")) ?? 0,
  cntNoBaby: toNumber(pick(row, "cntNoBaby", "CntNoBaby")) ?? 0,
  packIdDoc: toNumber(pick(row, "packIdDoc", "PackIdDoc")) ?? 0,
  packDocnum: toNumber(pick(row, "packDocnum", "PackDocnum")),
  packDocdate: pick(row, "packDocdate", "PackDocdate") ?? null,
  sessionStart: pick(row, "sessionStart", "SessionStart") ?? null,
  symaSkidka: toNumber(pick(row, "symaSkidka", "SymaSkidka")) ?? 0,
  deposit: toNumber(pick(row, "deposit", "Deposit")) ?? 0,
  vidPitFirstDay: toNumber(pick(row, "vidPitFirstDay", "VidPitFirstDay")) ?? 0,
  branchName: pick(row, "branchName", "BranchName") ?? "",
  sessionId: pick(row, "sessionId", "SessionId") ?? "",
  needToSync: toFlag(pick(row, "needToSync", "NeedToSync")),
  transVid: toNumber(pick(row, "transVid", "TransVid")) ?? 0,
  transName: pick(row, "transName", "TransName") ?? "",
  externalId: pick(row, "externalId", "ExternalId") ?? "",
  externalNeedSync: toFlag(pick(row, "externalNeedSync", "ExternalNeedSync")),
  externalDeadline: pick(row, "externalDeadline", "ExternalDeadline") ?? null,
  pPhone: pick(row, "pPhone", "PPhone") ?? "",
  idClient: toNumber(pick(row, "idClient", "IdClient")) ?? 0,
  idNumber: toNumber(pick(row, "idNumber", "IdNumber")) ?? 0,
  idNumberCat: toNumber(pick(row, "idNumberCat", "IdNumberCat")) ?? 0,
  vidPutevki: toNumber(pick(row, "vidPutevki", "VidPutevki")) ?? 0,
  idBranch: toNumber(pick(row, "idBranch", "IdBranch")) ?? 0,
  idUsrStatus: toNumber(pick(row, "idUsrStatus", "IdUsrStatus")) ?? 0
});

export const SEX_OPTIONS = [
  { value: 0, label: "Женский" },
  { value: 1, label: "Мужской" }
];

export const VID_MESTA_OPTIONS = [
  { value: 0, label: "Основное" },
  { value: 1, label: "Дополнительное" }
];

export const emptyClient = () => ({
  idStr: null,
  name: "",
  dateBird: null,
  sex: 0,
  isBaby: false,
  phone: "",
  email: "",
  pasport: "",
  adres: "",
  komment: "",
  docnum: 0,
  vidMesta: 0,
  vidLech: 0,
  workPlace: "",
  workPosition: "",
  smsAgree: false,
  skidkaAcc: 0,
  skidkaPit: 0,
  skidkaLech: 0,
  idNumber: 0,
  idNumberCat: 0,
  numberName: "",
  catName: "",
  dateBeg: null,
  dateEnd: null,
  idPred: 0,
  predName: "",
  sotrName: "",
  firstVisit: null,
  firstVisitZal: "",
  firstVisitUsl: ""
});

export const normalizeClient = (row) => ({
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  name: pick(row, "name", "Name") ?? "",
  dateBird: pick(row, "dateBird", "DateBird") ?? null,
  sex: toNumber(pick(row, "sex", "Sex")) ?? 0,
  isBaby: toFlag(pick(row, "isBaby", "IsBaby")),
  phone: pick(row, "phone", "Phone") ?? "",
  email: pick(row, "email", "Email") ?? "",
  pasport: pick(row, "pasport", "Pasport") ?? "",
  adres: pick(row, "adres", "Adres") ?? "",
  komment: pick(row, "komment", "Komment") ?? "",
  docnum: toNumber(pick(row, "docnum", "Docnum")) ?? 0,
  vidMesta: toNumber(pick(row, "vidMesta", "VidMesta")) ?? 0,
  vidLech: toNumber(pick(row, "vidLech", "VidLech")) ?? 0,
  workPlace: pick(row, "workPlace", "WorkPlace") ?? "",
  workPosition: pick(row, "workPosition", "WorkPosition") ?? "",
  smsAgree: toFlag(pick(row, "smsAgree", "SmsAgree")),
  skidkaAcc: toNumber(pick(row, "skidkaAcc", "SkidkaAcc")) ?? 0,
  skidkaPit: toNumber(pick(row, "skidkaPit", "SkidkaPit")) ?? 0,
  skidkaLech: toNumber(pick(row, "skidkaLech", "SkidkaLech")) ?? 0,
  idNumber: toNumber(pick(row, "idNumber", "IdNumber")) ?? 0,
  idNumberCat: toNumber(pick(row, "idNumberCat", "IdNumberCat")) ?? 0,
  numberName: pick(row, "numberName", "NumberName") ?? "",
  catName: pick(row, "catName", "CatName") ?? "",
  dateBeg: pick(row, "dateBeg", "DateBeg") ?? null,
  dateEnd: pick(row, "dateEnd", "DateEnd") ?? null,
  idClient: toNumber(pick(row, "idClient", "IdClient")) ?? 0,
  idSotr: toNumber(pick(row, "idSotr", "IdSotr")) ?? 0,
  dateVizit: pick(row, "dateVizit", "DateVizit") ?? null,
  sotrName: pick(row, "sotrName", "SotrName") ?? "",
  firstVisit: pick(row, "firstVisit", "FirstVisit") ?? null,
  firstVisitZal: pick(row, "firstVisitZal", "FirstVisitZal") ?? "",
  firstVisitUsl: pick(row, "firstVisitUsl", "FirstVisitUsl") ?? "",
  idPred: toNumber(pick(row, "idPred", "IdPred")) ?? 0,
  predName: pick(row, "predName", "PredName") ?? "",
  keyCardShort: pick(row, "keyCardShort", "KeyCardShort") ?? ""
});

/** Только цифры телефона; для сравнения 8XXXXXXXXXX ≡ 7XXXXXXXXXX. */
export const phoneKey = (value) => {
  const d = String(value || "").replace(/\D/g, "");
  if (d.length === 11 && d[0] === "8") return `7${d.slice(1)}`;
  return d;
};

/** Категория строки DOC_EXP_CNT.IS_AUTO (как в Delphi). Редактируются только 0. */
export const CNT_IS_AUTO = {
  EXTRA: 0,
  PLACEMENT: 1,
  FOOD: 2,
  TREATMENT: 3
};

export const CNT_IS_AUTO_LABELS = {
  [CNT_IS_AUTO.EXTRA]: "Дополнительно",
  [CNT_IS_AUTO.PLACEMENT]: "Размещение",
  [CNT_IS_AUTO.FOOD]: "Питание",
  [CNT_IS_AUTO.TREATMENT]: "Лечение"
};

export const CNT_IS_AUTO_TAG_COLOR = {
  [CNT_IS_AUTO.EXTRA]: "blue",
  [CNT_IS_AUTO.PLACEMENT]: "green",
  [CNT_IS_AUTO.FOOD]: "orange",
  [CNT_IS_AUTO.TREATMENT]: "purple"
};

export const cntLineLabel = (row) => {
  const name = (row?.tovName || "").trim();
  if (name) return name;
  const isAuto = toNumber(row?.isAuto) ?? 0;
  return CNT_IS_AUTO_LABELS[isAuto] || "Услуга";
};

export const emptyCnt = () => ({
  idStr: null,
  idTov: undefined,
  tovName: "",
  zenaExp: 0,
  kol: 1,
  symaExp: 0,
  prcDiscount: 0,
  symaDiscount: 0,
  symaPlat: 0,
  symaDolg: 0,
  fdate: null,
  komment: "",
  isAuto: CNT_IS_AUTO.EXTRA
});

export const normalizeCnt = (row) => {
  const isAuto = toNumber(pick(row, "isAuto", "IsAuto")) ?? 0;
  return {
    idStr: toNumber(pick(row, "idStr", "IdStr")),
    idTov: toNumber(pick(row, "idTov", "IdTov")) ?? 0,
    tovName: pick(row, "tovName", "TovName") ?? "",
    zenaExp: toNumber(pick(row, "zenaExp", "ZenaExp")) ?? 0,
    kol: toNumber(pick(row, "kol", "Kol")) ?? 0,
    symaExp: toNumber(pick(row, "symaExp", "SymaExp")) ?? 0,
    prcDiscount: toNumber(pick(row, "prcDiscount", "PrcDiscount")) ?? 0,
    symaDiscount: toNumber(pick(row, "symaDiscount", "SymaDiscount")) ?? 0,
    symaPlat: toNumber(pick(row, "symaPlat", "SymaPlat")) ?? 0,
    symaDolg: toNumber(pick(row, "symaDolg", "SymaDolg")) ?? 0,
    fdate: pick(row, "fdate", "Fdate") ?? null,
    komment: pick(row, "komment", "Komment") ?? "",
    isAuto
  };
};

export const isCntEditable = (cnt) => (toNumber(cnt?.isAuto) ?? 0) === CNT_IS_AUTO.EXTRA;

export const normalizeCard = (row) => ({
  idDoc: toNumber(pick(row, "idDoc", "IdDoc")),
  docdate: pick(row, "docdate", "Docdate") ?? null,
  docnum: toNumber(pick(row, "docnum", "Docnum")) ?? 0,
  komment: pick(row, "komment", "Komment") ?? "",
  syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
  symaPlat: toNumber(pick(row, "symaPlat", "SymaPlat")) ?? 0,
  symaDolg: toNumber(pick(row, "symaDolg", "SymaDolg")) ?? 0,
  avansOpen: toNumber(pick(row, "avansOpen", "AvansOpen")) ?? 0,
  idClient: toNumber(pick(row, "idClient", "IdClient")) ?? 0,
  predName: pick(row, "predName", "PredName") ?? "",
  isClosed: toFlag(pick(row, "isClosed", "IsClosed")),
  idNumber: toNumber(pick(row, "idNumber", "IdNumber")) ?? 0,
  numberName: pick(row, "numberName", "NumberName") ?? "",
  idNumberCat: toNumber(pick(row, "idNumberCat", "IdNumberCat")) ?? 0,
  catName: pick(row, "catName", "CatName") ?? "",
  dateBeg: pick(row, "dateBeg", "DateBeg") ?? null,
  dateEnd: pick(row, "dateEnd", "DateEnd") ?? null,
  dateBron: pick(row, "dateBron", "DateBron") ?? null,
  dateBegFact: pick(row, "dateBegFact", "DateBegFact") ?? null,
  dateEndFact: pick(row, "dateEndFact", "DateEndFact") ?? null,
  vidPutevki: toNumber(pick(row, "vidPutevki", "VidPutevki")) ?? 0,
  idSkidka: toNumber(pick(row, "idSkidka", "IdSkidka")) ?? 0,
  prcSkidka: toNumber(pick(row, "prcSkidka", "PrcSkidka")) ?? 0,
  symaSkidka: toNumber(pick(row, "symaSkidka", "SymaSkidka")) ?? 0,
  prcSkidkaPit: toNumber(pick(row, "prcSkidkaPit", "PrcSkidkaPit")) ?? 0,
  prcSkidkaLech: toNumber(pick(row, "prcSkidkaLech", "PrcSkidkaLech")) ?? 0,
  idStatus: toNumber(pick(row, "idStatus", "IdStatus")) ?? 0,
  statusName: pick(row, "statusName", "StatusName") ?? "",
  idUsrStatus: toNumber(pick(row, "idUsrStatus", "IdUsrStatus")) ?? 0,
  vidPitania: toNumber(pick(row, "vidPitania", "VidPitania")) ?? 0,
  e1: toFlag(pick(row, "e1", "E1")),
  e2: toFlag(pick(row, "e2", "E2")),
  e3: toFlag(pick(row, "e3", "E3")),
  e4: toFlag(pick(row, "e4", "E4")),
  vidPitFirstDay: toNumber(pick(row, "vidPitFirstDay", "VidPitFirstDay")) ?? 0,
  idBranch: toNumber(pick(row, "idBranch", "IdBranch")) ?? 0,
  avtorName: pick(row, "avtorName", "AvtorName") ?? "",
  sessionId: pick(row, "sessionId", "SessionId") ?? "",
  canRecalculate: (() => {
    const raw = pick(row, "canRecalculate", "CanRecalculate");
    if (raw === undefined || raw === null) return true;
    return raw !== false && raw !== 0 && raw !== "0" && raw !== "false";
  })(),
  recalculateBlockReason: pick(row, "recalculateBlockReason", "RecalculateBlockReason") ?? "",
  seats: (() => {
    const s = pick(row, "seats", "Seats");
    if (!s) return null;
    return {
      capacityOsn: toNumber(pick(s, "capacityOsn", "CapacityOsn")) ?? 0,
      capacityDop: toNumber(pick(s, "capacityDop", "CapacityDop")) ?? 0,
      otherOsn: toNumber(pick(s, "otherOsn", "OtherOsn")) ?? 0,
      otherDop: toNumber(pick(s, "otherDop", "OtherDop")) ?? 0,
      source: pick(s, "source", "Source") ?? ""
    };
  })(),
  clients: (pick(row, "clients", "Clients") || []).map(normalizeClient),
  cnts: (pick(row, "cnts", "Cnts") || []).map(normalizeCnt)
});

/** Сводка по местам: used из клиентов формы, лимиты из seats. */
export const seatsSummary = (clients, seats) => {
  const list = Array.isArray(clients) ? clients : [];
  const usedOsn = list.filter((c) => (Number(c?.vidMesta) || 0) === 0).length;
  const usedDop = list.length - usedOsn;
  if (!seats) return { usedOsn, usedDop, availOsn: null, availDop: null, overOsn: false, overDop: false, seats: null };
  const availOsn = Math.max(seats.capacityOsn - seats.otherOsn, 0);
  const availDop = Math.max(seats.capacityDop - seats.otherDop, 0);
  return {
    usedOsn, usedDop, availOsn, availDop, seats,
    overOsn: seats.capacityOsn > 0 && usedOsn > availOsn,
    overDop: usedDop > availDop
  };
};

export const seatsErrorText = (summary) => {
  if (!summary) return "";
  if (summary.overOsn) return `Превышено количество основных мест: ${summary.usedOsn} из ${summary.availOsn}`;
  if (summary.overDop) return `Превышено количество дополнительных мест: ${summary.usedDop} из ${summary.availDop}`;
  return "";
};

export const emptyCard = () => ({
  idDoc: null,
  docdate: todayIso(),
  docnum: 0,
  komment: "",
  syma: 0,
  symaPlat: 0,
  symaDolg: 0,
  avansOpen: 0,
  idClient: 0,
  predName: "",
  dateBeg: null,
  dateEnd: null,
  dateBron: null,
  vidPutevki: 0,
  idSkidka: 0,
  prcSkidka: 0,
  symaSkidka: 0,
  prcSkidkaPit: 0,
  prcSkidkaLech: 0,
  idStatus: 0,
  idUsrStatus: 0,
  vidPitania: 0,
  clients: [],
  e1: true,
  e2: true,
  e3: true,
  e4: true,
  vidPitFirstDay: 0,
  idBranch: 0,
  idNumber: 0,
  idNumberCat: 0,
  cnts: []
});

export const isCntDeletable = (cnt) =>
  isCntEditable(cnt) && Math.abs(toNumber(cnt?.symaPlat) ?? 0) < 0.005;

export const mealFlagsFromRazmer = (razmer) => {
  const s = String(Math.round(Number(razmer) || 0)).padStart(4, "0").slice(-4);
  return { e1: s[0] === "1", e2: s[1] === "1", e3: s[2] === "1", e4: s[3] === "1" };
};

export const normalizeLookups = (data) => {
  const mapItems = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((row) => ({
        value: toNumber(pick(row, "id", "Id")),
        label: pick(row, "name", "Name") ?? "",
        razmer: toNumber(pick(row, "razmer", "Razmer")) ?? 0
      }))
      .filter((o) => o.value != null);

  return {
    vidPutevki: [{ value: 0, label: "—" }, ...mapItems(pick(data, "vidPutevki", "VidPutevki"))],
    skidki: [{ value: 0, label: "—" }, ...mapItems(pick(data, "skidki", "Skidki"))],
    vidPitania: [{ value: 0, label: "—" }, ...mapItems(pick(data, "vidPitania", "VidPitania"))],
    vidLech: [{ value: 0, label: "—" }, ...mapItems(pick(data, "vidLech", "VidLech"))],
    branches: [{ value: 0, label: "—" }, ...mapItems(pick(data, "branches", "Branches"))],
    usrStatuses: [{ value: 0, label: "—" }, ...mapItems(pick(data, "usrStatuses", "UsrStatuses"))]
  };
};

export const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};
